import { useSearchParams } from "eddev/routing"
import { useProgramFilms } from "./queries"
import { useEffect, useMemo } from "react"
import { FilmTileProps } from "@components/film/FilmTile"
import { EventType, FilmSessionFragment, FilmTileWithSessionsFragment, VenueTileFragment } from "../types.graphql"
import dayjs from "dayjs"
import { useRPCQuery } from "eddev/hooks"

export type FilmSearchItemsType = (FilmTileProps & { key: string })[]
export type FilmSearchMode = "a-z" | "date" | "calendar" | "shorts"

export const filmsWithSessions = (films: (FilmTileWithSessionsFragment | null)[] | null | undefined) => {
  return (
    films?.flatMap((film) => {
      if (!film?.sessions?.length) return []
      return film.sessions.map((session) => {
        return {
          film: film!,
          session: session!,
          key: film!.uri! + "-" + session?.id!,
        }
      })
    })! || []
  )
}

export function useFilmSearch({ programID }: { programID: string }) {
  const lookup = useRPCQuery("api.program.films", {
    program: programID,
  })

  const [params, paramsApi] = useSearchParams({
    mode: null as string | null,
    venues: null as string[] | null,
    countries: null as string[] | null,
    genres: null as string[] | null,
    directors: null as string[] | null,
    search: null as string | null,
    themes: null as string[] | null,
    categories: null as string[] | null,
  })

  const search = params.search
  const mode = (params.mode as FilmSearchMode) || "a-z"

  const disableFilters = mode === "calendar"

  const filmList = useMemo(() => {
    if (!lookup.data) return null
    if (search && search !== "") {
      return lookup.data?.program?.films?.filter((film) =>
        [EventType.Film, EventType.MultiFilm, EventType.Short, EventType.Event, EventType.Other].includes(
          film?.eventInfo?.eventType!,
        ),
      )
    } else if (mode === "shorts") {
      return lookup.data?.program?.films?.filter((film) =>
        [EventType.Short, EventType.MultiFilm].includes(film?.eventInfo?.eventType!),
      )
    } else {
      return lookup.data?.program?.films?.filter((film) =>
        [EventType.Film, EventType.MultiFilm, EventType.Event, EventType.Other].includes(film?.eventInfo?.eventType!),
      )
    }
  }, [search, mode, lookup.data])

  const items = useMemo(() => {
    let items: {
      film: FilmTileWithSessionsFragment
      session?: FilmSessionFragment
      key: string
    }[] = []

    if (filmList) {
      if (mode === "a-z") {
        items = filmList?.map((film) => ({
          film: film!,
          key: film!.uri!,
        }))!
      } else if (mode === "shorts") {
        items = filmList?.map((film) => ({
          film: film!,
          key: film!.uri!,
        }))!
      } else if (mode === "date" || mode === "calendar") {
        items = filmsWithSessions(filmList)
      }

      // Sort alphabetically first
      if (mode === "a-z" || mode === "shorts") {
        items.sort((a, b) => a.film.title?.replace(/^The /, "")?.localeCompare(b.film.title?.replace(/^The /, "")!)!)
      }

      // For shorts, move 'Shorts Blocks' (MultiEvent) to the top
      if (mode === "shorts") {
        items.sort((a, b) => {
          const aType = a.film.eventInfo?.eventType
          const bType = b.film.eventInfo?.eventType
          if (aType === bType) return 0
          if (aType === EventType.MultiFilm) return -1
          if (bType === EventType.MultiFilm) return 1
          return 0
        })
      }

      // Sort by date
      if (mode === "calendar" || mode === "date") {
        items.sort((a, b) => a.session?.info?.dateTime!.localeCompare(b.session?.info?.dateTime!)!)

        // // don't show past films
        items = items.filter((item) => {
          return dayjs.tz(item.session?.info?.dateTime!).isAfter(dayjs.tz().subtract(2, "hour"))
        })
      }

      if (!disableFilters && params.venues && params.venues.length) {
        items = items.filter((item) => {
          let found = false
          params.venues?.forEach((venue) => {
            if (item.film.sessions?.length) {
              item.film.sessions?.forEach((session) => {
                if (session?.info?.venue?.title?.includes(venue) || venue === "all") found = true
              })
            } else {
              if (item.session?.info?.venue?.title?.includes(venue) || venue === "all") found = true
            }
          })
          return found
        })
      }

      if (!disableFilters && params.countries && params.countries.length) {
        items = items.filter((item) => {
          const g = item.film.filmInfo?.country
          let found = false
          params.countries?.forEach((country) => {
            if (g?.includes(country) || country === "all") found = true
          })
          return found
        })
      }

      if (!disableFilters && params.genres && params.genres.length) {
        items = items.filter((item) => {
          const g = item.film.filmInfo?.genre
          let found = false
          params.genres?.forEach((genre) => {
            if (g?.includes(genre) || genre === "all") found = true
          })
          return found
        })
      }

      if (!disableFilters && params.themes && params.themes.length) {
        items = items.filter((item) => {
          const g = item.film.filmInfo?.theme
          let found = false
          params.themes?.forEach((theme) => {
            if (g?.includes(theme) || theme === "all") found = true
          })
          return found
        })
      }

      if (!disableFilters && params.categories && params.categories.length) {
        items = items.filter((item) => {
          const g = item.film.eventInfo?.filmTypeLabel
          let found = false
          params.categories?.forEach((cat) => {
            if (g?.includes(cat) || cat === "all") found = true
          })
          return found
        })
      }

      if (!disableFilters && params.search) {
        items = items.filter((item) => {
          return (
            item.film.title
              ?.toLowerCase()
              .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, "")
              .includes(params.search?.toLowerCase().replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, "")!) ||
            item.film.filmInfo?.country?.includes(params.search!) ||
            item.film.filmInfo?.genre?.includes(params.search!) ||
            item.film.filmInfo?.theme?.includes(params.search!)
          )
        })
      }
    }

    return items
  }, [params, filmList, disableFilters])

  const itemVenues = useMemo(() => {
    const items = filmsWithSessions(filmList)
    const i = items?.map((item) => {
      const v = (item.session?.info?.venue?.parent?.node as VenueTileFragment) || item.session?.info?.venue
      return {
        title: v?.title,
        databaseId: v?.databaseId,
      }
    })
    return i
      .filter((v, i, a) => v.title && a.findIndex((t) => t.title === v.title) === i)
      .sort((a, b) => a.title!.localeCompare(b.title!))
  }, [filmList])

  const itemCountries = useMemo(() => {
    const i =
      filmList?.map((item) => {
        const g = item?.filmInfo?.country || []
        return g
      }) ?? []
    return i
      .flat()
      .filter((g, i, a) => g && a.findIndex((t) => t === g) === i)
      .sort((a, b) => a!.localeCompare(b!))
  }, [filmList])

  const itemGenres = useMemo(() => {
    const i =
      filmList?.map((item) => {
        const g = item?.filmInfo?.genre || []
        return g
      }) ?? []
    return i
      .flat()
      .filter((g, i, a) => g && a.findIndex((t) => t === g) === i)
      .sort((a, b) => a!.localeCompare(b!))
  }, [filmList])

  const itemThemes = useMemo(() => {
    const i =
      filmList?.map((item) => {
        const g = item?.filmInfo?.theme || []
        return g
      }) ?? []
    return i
      .flat()
      .filter((g, i, a) => g && a.findIndex((t) => t === g) === i)
      .sort((a, b) => a!.localeCompare(b!))
  }, [filmList])

  const itemCategories = useMemo(() => {
    return [
      ...new Set(
        filmList
          ?.map((item) => {
            const g = item?.eventInfo?.filmTypeLabel
            return g
          })
          .flat()
          .filter(Boolean) ?? [],
      ),
    ].sort((a, b) => a!.localeCompare(b!))
  }, [filmList])

  const itemDirectors = useMemo(() => {
    return []
  }, [filmList])

  useEffect(() => {
    const types = {
      venues: itemVenues?.map((v) => v.title),
      countries: itemCountries,
      genres: itemGenres,
      themes: itemThemes,
      categories: itemCategories,
    }
    try {
      const newParams = { ...params }
      let didChange = false
      for (let key in types) {
        const availableOptions = (types as any)[key] as string[]
        const values = (params[key as keyof typeof params] ?? []) as string[]
        if (
          availableOptions.length > 0 &&
          Array.isArray(values) &&
          values.length > 0 &&
          values.some((v) => !availableOptions.includes(v))
        ) {
          ;(newParams as any)[key] = values.filter((v) => availableOptions.includes(v))
          didChange = true
        }
      }
      if (didChange) {
        paramsApi.setAll(newParams)
      }
    } catch (err) {}
  }, [params, itemVenues, itemCountries, itemGenres, itemThemes, itemCategories])

  return {
    status: lookup.status,
    disableFilters,
    params,
    paramsApi,
    items,
    mode,
    venues: itemVenues,
    countries: itemCountries,
    genres: itemGenres,
    themes: itemThemes,
    directors: itemDirectors,
    categories: itemCategories,
  }
}
